import type { UploadEntry } from '@shared/types/file-upload.types'
import { OptionalIndicator } from '@shared/ui/form-label-indicators'
import {
  Box,
  Column,
  FileDropzone,
  FileInput,
  FileInputLabel,
  FileTile,
  FileTileDeleteButton,
  FileTileThumbnail,
  FormField,
  FormHelper,
  FormInputGroup,
  FormLabel,
  GridContainer,
  Icon,
  ResponsiveImage,
  Row,
} from '@vp/swan'
import { useTranslation } from 'react-i18next'

interface Props {
  entries: UploadEntry[]
  addFiles: (files: File[]) => void
  removeEntry: (id: string) => void
}

export const FileUploadField = ({ entries, addFiles, removeEntry }: Props) => {
  const { t } = useTranslation()
  const handleChange = (e: React.ChangeEvent<HTMLInputElement, HTMLInputElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const input = e.target as any
    const files = Array.from(input.files ?? []) as File[]
    addFiles(files)
  }

  return (
    <FormField data-testid="file-upload-field">
      <FormLabel mt={5}>
        {t('contact_us.page.steps.issue_description.upload_label')}
        <OptionalIndicator />
      </FormLabel>

      <Box data-testid="file-upload-dropzone">
        <FormInputGroup>
          <FileDropzone
            multiFileErrorMessage={t('contact_us.page.steps.issue_description.upload_multi_error')}
            aria-label={t('contact_us.page.steps.issue_description.upload_label')}
          >
            <FileInput multiple accept=".jpg,.jpeg,.png,.gif,.bmp,.tif,.tiff" onChange={handleChange}>
              <FileInputLabel data-testid="file-upload-trigger" iconPosition="left">
                <Icon iconType="upload" />
                {t('contact_us.page.steps.issue_description.actions.upload')}
              </FileInputLabel>
            </FileInput>
          </FileDropzone>
          <FormHelper>{t('contact_us.page.steps.issue_description.upload_helper')}</FormHelper>
        </FormInputGroup>
      </Box>

      {entries.length > 0 && (
        <GridContainer mt={5}>
          <Row verticalGap="matchGutter">
            {entries.map((entry: UploadEntry) => (
              <Column key={entry.id} spanXs={6} spanSm={4} span={3}>
                <Box data-testid="file-upload-entry">
                  <FileTile
                    fileName={entry.asset?.fileName || entry.name}
                    skin={entry.status === 'uploading' ? 'loading' : 'standard'}
                    progressValue={entry.progress}
                  >
                    {entry.status === 'done' && entry.asset && (
                      <FileTileThumbnail>
                        <ResponsiveImage src={entry.asset.publicUrl} />
                      </FileTileThumbnail>
                    )}
                    <FileTileDeleteButton data-testid="file-upload-delete" onClick={() => removeEntry(entry.id)} />
                  </FileTile>
                </Box>
              </Column>
            ))}
          </Row>
        </GridContainer>
      )}
    </FormField>
  )
}
